import { Icons } from '@/components/icons';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import {
  downloadBookingTickets,
  downloadTicketDocumentByType,
} from '@/features/purchase/purchaseService';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import { TransTenantSpecific } from '@/i18n/trans/tenantSpecific';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import type { PurchaseFlowBookingStorageData } from '@/types/storage';
import { breakpoints } from '@/utils/breakpoints';
import {
  GTM_EVENT,
  mapBookingToEcommerceItems,
  trackEcommerceEvent,
} from '@/utils/gtm';
import { getTenantConfiguration } from '@/utils/tenant';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Success = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenant = useSelector(tenantSelector);
  const configuration = getTenantConfiguration(tenant.value);
  const booking = useSelector((state) => state.purchase.booking);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPkPassDownloading, setIsPkPassDownloading] = useState(false);
  const { value: successData, removeValue } =
    useStorage<PurchaseFlowBookingStorageData>(
      sessionStorage,
      STORAGE_KEYS.CHECKOUT_SUCCESS_DATA
    );
  const { setCurrentSearchFormValues } = useSearchWidget();
  const hasFiredPurchaseEvent = useRef(false);

  usePageTitle('purchase');

  useEffect(() => {
    if (!successData?.bookingId) {
      navigate('/purchase', { replace: true });
      return;
    }

    dispatch(resetPurchase());
    setCurrentSearchFormValues(undefined);
    persistor.purge();

    return () => {
      removeValue();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!successData?.bookingId) return;

    // Prefer the amount captured at payment time: the booking's own
    // confirmedPrice can still be 0 right after payment on backends that
    // confirm asynchronously, which would report zero revenue to GA4.
    const totalPrice = successData.totalPrice ?? booking?.confirmedPrice;
    if (
      !hasFiredPurchaseEvent.current &&
      configuration?.enableGoogleTagManager &&
      totalPrice?.currency
    ) {
      hasFiredPurchaseEvent.current = true;

      trackEcommerceEvent(GTM_EVENT.PURCHASE, {
        transaction_id: successData.bookingId,
        value: totalPrice.amount,
        currency: totalPrice.currency,
        items: mapBookingToEcommerceItems(booking),
      });
    }
  }, [booking, configuration?.enableGoogleTagManager, successData]);

  const handleTicketDownload = useCallback(async () => {
    if (!successData?.bookingId) {
      return;
    }

    try {
      setIsDownloading(true);
      await downloadBookingTickets(successData?.bookingId, successData?.code);
    } finally {
      setIsDownloading(false);
    }
  }, [successData?.bookingId, successData?.code]);

  const handlePkPassDownload = useCallback(async () => {
    if (!successData?.fulfillments?.length) {
      return;
    }

    try {
      setIsPkPassDownloading(true);
      await Promise.all(
        successData?.fulfillments.map(async (fulfillment) => {
          try {
            await downloadTicketDocumentByType(
              fulfillment,
              'PKPASS',
              successData?.code
            );
          } catch (error) {
            toast.error(<TransText i18nKey="errorDownloadingPkpass" />);
          }
        })
      );
    } catch (error) {
      toast.error(<TransText i18nKey="errorDownloadingPkpass" />);
    } finally {
      setIsPkPassDownloading(false);
    }
  }, [successData?.code, successData?.fulfillments]);

  if (!successData?.bookingId) {
    return null;
  }

  return (
    <CenteredLayout>
      <div className="mx-auto mt-10 flex w-full max-w-tablet items-center p-4">
        <div className="grid w-full grid-cols-1 gap-2 laptop:grid-cols-2 laptop:gap-10">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <Icons.checkCircle height={28} width={28} />
              <Typography
                variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
                asChild
              >
                <h1>
                  <TransText i18nKey="thankYou" />
                </h1>
              </Typography>
            </div>
            <Typography variant="paragraph" className="mt-2">
              <TransText
                i18nKey="bookingNo"
                values={{ number: successData.code }}
              />
            </Typography>
            <Divider className="mb-6 mt-4" />
            {configuration?.showSuccessMessage && (
              <Typography variant="body1">
                <TransTenantSpecific i18nKey="successMessage" />
              </Typography>
            )}
          </div>

          <div className="mt-4 laptop:mt-0">
            <div className="flex flex-col gap-4 py-3 tablet:flex-row tablet:items-center">
              <Button
                fullWidth
                className="laptop:w-auto"
                data-testid="download-pdf-button"
                variant="secondary"
                size="medium"
                onClick={handleTicketDownload}
                disabled={isDownloading || !successData.bookingId}
              >
                {isDownloading ? (
                  <Icons.loader
                    height={16}
                    width={16}
                    className="animate-spin"
                  />
                ) : (
                  <Icons.download height={16} width={16} />
                )}
                <TransText i18nKey="downloadTickets" />
              </Button>
              <Button
                fullWidth
                size="medium"
                className="laptop:w-auto"
                data-testid="download-pkpass-button"
                variant="secondary"
                onClick={handlePkPassDownload}
                disabled={isPkPassDownloading || !successData.bookingId}
              >
                {isPkPassDownloading ? (
                  <Icons.loader
                    height={16}
                    width={16}
                    className="animate-spin"
                  />
                ) : (
                  <Icons.pkPassWallet height={16} width={16} />
                )}
                <TransText i18nKey="addToWallet" />
              </Button>
            </div>
            <Divider className="mb-6 mt-4" />
            <Typography variant="body1">
              <TransTenantSpecific i18nKey="ticketSent" />
            </Typography>
          </div>
        </div>
      </div>
    </CenteredLayout>
  );
};

export default Success;
